import {
  IndividualAttraction,
  IndividualAttractionSourcePhoto,
  IndividualAttractionPhoto,
  GroupedAttractions,
  UploadCallback,
  CroppedImage,
} from 'lib/serializers/types';
import Config from 'config';

export const NATURAL_WIDTH = 560;
export const NATURAL_HEIGHT = 315;

export const sortByStarredAndPosition = (a: IndividualAttraction, b: IndividualAttraction) => {
  if (a.deleted !== b.deleted) {
    return a.deleted ? 1 : -1;
  }

  if (a.starred !== b.starred) {
    return +b.starred - +a.starred;
  }

  if (a.position === null) return 1;
  if (b.position === null) return -1;

  return a.position - b.position;
};

const setAttractionStatus = (attraction: IndividualAttraction) => ({
  ...attraction,
  status: attraction.hidden ? 'hidden' : '',
});

export const serializeInputAttractions = (individualAttractions: IndividualAttraction[]) =>
  getAttractionsWithPosition(individualAttractions).map(({ photos, ...individualAttraction }) => ({
    ...individualAttraction,
    photo: serializeInputPhotos(photos || []),
  }));

export const getAttractionsWithPosition = (individualAttractions: IndividualAttraction[]) => individualAttractions
  .sort(sortByStarredAndPosition)
  .map((individualAttraction, index) => ({
    ...individualAttraction,
    position: index,
  }));

const groupIndividualAttractions = (
  individualAttractions: IndividualAttraction[]
): GroupedAttractions | null => {
  if (!individualAttractions?.length) {
    return null;
  }
  const attractionsWithStatus = individualAttractions.map(setAttractionStatus);

  return {
    headliners: attractionsWithStatus.filter(({ starred }) => starred),
    lineup: attractionsWithStatus.filter(({ starred }) => !starred),
    all: attractionsWithStatus,
  };
};

export const transformIndividualAttractions = (individualAttractions: IndividualAttraction[]) => {
  if (!individualAttractions?.length) {
    return null;
  }

  const filteredAttractions = individualAttractions.filter(({ deleted }) => !deleted);

  return groupIndividualAttractions(filteredAttractions);
};

export const serializeOutputAttractions = (value: IndividualAttraction[]) => {
  return value
    .map(({
      id,
      bio,
      isNew,
      starred,
      hidden,
      deleted,
      position,
      discoveryId,
      data,
      photo,
      embeddedWidgetCode,
    }) => ({
      id: isNew ? null : id,
      bio,
      discoveryId,
      position,
      starred,
      hidden,
      destroy: deleted,
      data,
      photos: serializeOutputPhotos(photo || null),
      embeddedWidgetCode,
    }));
};

export const serializeInputPhotos = ([photo]: IndividualAttractionSourcePhoto[]): IndividualAttractionPhoto | null => {
  return photo ? {
    croppedImage: {
      height: photo.cropHeight,
      width: photo.cropWidth,
      x: photo.cropX,
      y: photo.cropY,
    },
    naturalWidth: NATURAL_WIDTH,
    naturalHeight: NATURAL_HEIGHT,
    uploadId: photo.uploadcareId,
    imageUrl: `${Config.images.url}${photo.uploadcareId}/`,
    url: photo.url,
    id: photo.id,
    is_gif: photo.isGif,
    destroy: photo.destroy,
  } : null;
};

export const serializeOutputPhotos = (photo: IndividualAttractionPhoto | null): IndividualAttractionSourcePhoto[] => {
  return photo ? [{
    cropHeight: photo.croppedImage?.height,
    cropWidth: photo.croppedImage?.width,
    cropX: photo.croppedImage?.x,
    cropY: photo.croppedImage?.y,
    id: photo.id,
    isGif: photo.is_gif,
    uploadcareId: photo.uploadId,
    destroy: photo.destroy,
  }] : [];
};

export const uploadImageByUrl = async (imgUrl: string, uploadCallback: UploadCallback) => {
  try {
    const data = await uploadCallback(imgUrl, {
      publicKey: Config.uploadcare.key,
    });

    return {
      imageUrl: `${Config.images.url}${data.uuid}/`,
      uploadId: data.uuid,
      imageWidth: data?.imageInfo?.width,
      imageHeight: data?.imageInfo?.height,
      isGif: data?.contentInfo?.image?.format === 'GIF',
      croppedImage: {
        width: (data?.imageInfo?.width || 0) / 2,
        height: (data?.imageInfo?.height || 0) / 2,
        x: 0,
        y: 0,
      },
    };
  } catch ({ message }) {
    throw new Error(`Failed to upload photo, ${message}`);
  }
};

export const checkForHiddenSubtitles = (attractions: GroupedAttractions) => {
  return (
    attractions &&
      Object
        .values(attractions)
        .some(group => group.every(({ hidden }: IndividualAttraction) => hidden))
  );
};

export const shouldDisableMainTitle = (attractions: IndividualAttraction[]) =>
  attractions?.length && attractions.every(({ hidden }) => hidden);

export const generateImageUrl = (imageId: string, { width, height, x = 0, y = 0 }: CroppedImage) => {
  return `${imageId}/-/resize/1200x/-/crop/${width}x${height}/${x},${y}/`;
};

export const checkPhotoValidity = (attractionPhoto: IndividualAttractionSourcePhoto) =>
  !!attractionPhoto && !attractionPhoto?.destroy;

export const setNullIfEmpty = (condition: boolean, property: string) => condition ? property : null;
